import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  AdvancedFilterDataSchema,
  FilterGroup,
  FilterGroupSchema,
} from "./advanced-filters/advanced-filter-data";
import { ArrayStringFilterBuilder } from "./advanced-filters/array-string-filter";
import { DateFilterBuilder } from "./advanced-filters/date-filter";
import { isFilterGroup } from "./advanced-filters/generic-advanced-filter-data";
import { NumberFilterBuilder } from "./advanced-filters/number-filter";
import { StringFilterBuilder } from "./advanced-filters/string-filter";
import { SortDirection } from "./table";

export const DEFAULT_PAGE_SIZE = 50;

export class InvalidViewNameError extends Error {
  constructor(name: string, reason: string) {
    super(`Invalid view or folder name ${JSON.stringify(name)}: ${reason}`);
    this.name = "InvalidViewNameError";
  }
}

const DOT_ONLY_NAME = /^\.+$/;
const CONTROL_CHARS = /[\x00-\x1f\x7f]/;

// Names reserved by the system — exact case-insensitive matches are rejected.
// Includes static URL route siblings to :viewName (routing conflicts) and
// system-seeded view names (DB name conflicts).
export const RESERVED_VIEW_NAMES = new Set([
  // Static route siblings to :viewName in the order section
  "about",
  "admin-pick-sessions",
  "bad-warehouse-bins",
  "batch-history",
  "bulk-fulfillments",
  "create-pick-session",
  "dashboard",
  "downloads",
  "eod-manifest",
  "lookup",
  "orders",
  "packing-slips",
  "personalized-slips",
  "pick-sessions",
  "pickups",
  "print-jobs",
  "rate-calculator",
  "rewards",
  "scan",
  "tariff-calculator",
  // System-seeded view names
  "active",
  "all",
  "all-recycle",
  "all-returns",
  "all-warranties",
  "assigned",
  "awaiting-processing",
  "awaiting-remit",
  "awaiting-shipment",
  "closed",
  "complete",
  "completed",
  "delivered",
  "draft",
  "drafts",
  "exceptions",
  "failed-rma-creation",
  "fast pass",
  "flagged",
  "forecasting",
  "in-progress",
  "in-store-pickup",
  "international",
  "in-transit",
  "label-creation-failed",
  "managed",
  "mentions",
  "merchant-review",
  "needs-review",
  "not-started",
  "open",
  "ordered",
  "out-for-delivery",
  "out-of-stock",
  "outstanding-balance",
  "partially-processed",
  "partially-received",
  "pending",
  "priority",
  "received",
  "refund-payment-failed",
  "rejected",
  "repairs",
  "reset",
  "restock-failed",
  "sent",
  "shipments",
  "spam",
  "stuck-in-transit",
  "tracking-exception",
]);

export interface ViewNameValidationOptions {
  skipReservedCheck?: boolean;
}

/**
 * Returns a human-readable validation error if the name is invalid for use as
 * a view or folder name, otherwise undefined. Use this for UI form validation.
 */
export function getViewNameValidationError(
  name: string,
  options?: ViewNameValidationOptions,
): string | undefined {
  if (name.trim().length === 0) {
    return "Name cannot be empty";
  }
  if (DOT_ONLY_NAME.test(name.trim())) {
    return "Name cannot consist only of dots";
  }
  if (CONTROL_CHARS.test(name)) {
    return "Name cannot contain control characters";
  }
  if (
    !options?.skipReservedCheck &&
    RESERVED_VIEW_NAMES.has(name.trim().toLowerCase())
  ) {
    return "Name cannot be a reserved system view name";
  }
  return undefined;
}

export function assertValidViewName(
  name: string,
  options?: ViewNameValidationOptions,
): void {
  const error = getViewNameValidationError(name, options);
  if (error !== undefined) {
    throw new InvalidViewNameError(name, error);
  }
}

// Valid page sizes allowed by table2 component
export const VALID_PAGE_SIZES = [
  10, 25, 50, 100, 150, 200, 250, 500, 1000, 1500,
] as const;
export type ValidPageSize = (typeof VALID_PAGE_SIZES)[number];

export const ViewSortSchema = z.object({
  direction: createEnum(SortDirection, { enumName: "SortDirection" }),
  key: z.string(),
});
export type ViewSort = z.infer<typeof ViewSortSchema>;

export const ViewColumnSchema = z.object({
  key: z.string(),
  width: z.number(),
  hidden: z.boolean(),
  pinned: z.boolean().optional(),
});
export type ViewColumn = z.infer<typeof ViewColumnSchema>;

const BaseViewSchema_ = z.object({
  _id: z.string().nullish(),
  table: z.string(),
  name: z.string(),
  filters: z.array(AdvancedFilterDataSchema),
  filterGroup: FilterGroupSchema.nullish(),
  columns: z.array(ViewColumnSchema),
  sort: ViewSortSchema.nullish(),
  pageSize: z.number().nullish(),
  folderId: z.string().nullish(),
  position: z.number(),
  deleted: z.boolean().nullish(),
  isNotEditable: z.boolean().optional(),
});
export const BaseViewSchema: z.ZodType<BaseView> = BaseViewSchema_;
export interface BaseView extends z.infer<typeof BaseViewSchema_> {}

export function countFilterClauses(group: FilterGroup): number {
  let count = 0;
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      count += countFilterClauses(clause);
    } else {
      count += 1;
    }
  }
  return count;
}

export function getFilterCount(view: BaseView): number {
  if (view.filterGroup) {
    return countFilterClauses(view.filterGroup);
  }
  return view.filters.length;
}

/**
 * Document-level schema representing how views are stored in MongoDB.
 * The view data is nested under the `view` field.
 */
export const ViewDocumentSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  table: z.string(),
  name: z.string(),
  view: BaseViewSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type ViewDocument = z.infer<typeof ViewDocumentSchema>;

const BaseViewWithNotificationsSchema_ = BaseViewSchema_.extend({
  showNotification: z.boolean().default(false),
  hideWhenEmpty: z.boolean().optional(),
});
export const BaseViewWithNotificationsSchema: z.ZodType<BaseViewWithNotifications> =
  BaseViewWithNotificationsSchema_;
export interface BaseViewWithNotifications extends z.infer<
  typeof BaseViewWithNotificationsSchema_
> {}

const PickSessionPriorityViewSchema_ = BaseViewSchema_.extend({
  percentage: z.number().min(0).max(100),
});
export const PickSessionPriorityViewSchema: z.ZodType<PickSessionPriorityView> =
  PickSessionPriorityViewSchema_;
export interface PickSessionPriorityView extends z.infer<
  typeof PickSessionPriorityViewSchema_
> {}

// Map view types to a Mongoose model and a Typescript interface
export const viewSchemas = {
  "fulfillment-groups": BaseViewSchema,
  "bulk-fulfillments": BaseViewSchema,
  "pick-session": BaseViewSchema,
  "pick-session-priority": PickSessionPriorityViewSchema,
  "oms-priority": BaseViewSchema,
  returns: BaseViewWithNotificationsSchema,
  claims: BaseViewWithNotificationsSchema,
  warranties: BaseViewWithNotificationsSchema,
  recycle: BaseViewWithNotificationsSchema,
  withdrawals: BaseViewWithNotificationsSchema,
  registrations: BaseViewSchema,
  "purchase-orders": BaseViewSchema,
  shipments: BaseViewSchema,
  transfers: BaseViewSchema,
  "inventory-levels": BaseViewSchema,
  "catalog-products": BaseViewSchema,
  "order-tracking-shipments": BaseViewSchema,
  "order-tracking-orders": BaseViewSchema,
  "work-orders": BaseViewSchema,
  "work-order-priority": PickSessionPriorityViewSchema,
  "replenishment-requests": BaseViewSchema,
  "support-v2-conversations": BaseViewSchema,
};

export enum ViewTypesNames {
  FULFILLMENT_GROUPS = "fulfillment-groups",
  BULK_FULFILLMENTS = "bulk-fulfillments",
  PICK_SESSION = "pick-session",
  PICK_SESSION_PRIORITY = "pick-session-priority",
  WORK_ORDER_PRIORITY = "work-order-priority",
  OMS_PRIORITY = "oms-priority",
  RETURNS = "returns",
  CLAIMS = "claims",
  WARRANTIES = "warranties",
  RECYCLE = "recycle",
  WITHDRAWALS = "withdrawals",
  REGISTRATIONS = "registrations",
  PURCHASE_ORDERS = "purchase-orders",
  SHIPMENTS = "shipments",
  TRANSFERS = "transfers",
  INVENTORY_LEVELS = "inventory-levels",
  CATALOG_PRODUCTS = "catalog-products",
  ORDER_TRACKING_SHIPMENTS = "order-tracking-shipments",
  ORDER_TRACKING_ORDERS = "order-tracking-orders",
  WORK_ORDERS = "work-orders",
  REPLENISHMENT_REQUESTS = "replenishment-requests",
  SUPPORT_V2_CONVERSATIONS = "support-v2-conversations",
}

export type viewTypes = {
  [key in keyof typeof viewSchemas]: z.infer<(typeof viewSchemas)[key]>;
};

export type FilterBuilder =
  | typeof DateFilterBuilder
  | typeof ArrayStringFilterBuilder
  | typeof NumberFilterBuilder
  | typeof StringFilterBuilder;

const baseViewWithIdSchema_ = BaseViewSchema_.extend({
  _id: z.string(),
  updatedAt: z.coerce.date().nullish(),
});
export const baseViewWithIdSchema: z.ZodType<BaseViewWithId> =
  baseViewWithIdSchema_;
export interface BaseViewWithId extends z.infer<typeof baseViewWithIdSchema_> {}

const baseViewWithNotificationsAndIdSchema_ =
  BaseViewWithNotificationsSchema_.extend({ _id: z.string() });
export const baseViewWithNotificationsAndIdSchema: z.ZodType<BaseViewWithNotificationsAndId> =
  baseViewWithNotificationsAndIdSchema_;
export interface BaseViewWithNotificationsAndId extends z.infer<
  typeof baseViewWithNotificationsAndIdSchema_
> {}

const pickSessionPriorityViewWithIdSchema_ =
  PickSessionPriorityViewSchema_.extend({ _id: z.string() });
export const pickSessionPriorityViewWithIdSchema: z.ZodType<PickSessionPriorityViewWithId> =
  pickSessionPriorityViewWithIdSchema_;
export interface PickSessionPriorityViewWithId extends z.infer<
  typeof pickSessionPriorityViewWithIdSchema_
> {}

export const viewWithIdSchemas = {
  "fulfillment-groups": baseViewWithIdSchema,
  "bulk-fulfillments": baseViewWithIdSchema,
  "pick-session": baseViewWithIdSchema,
  "pick-session-priority": pickSessionPriorityViewWithIdSchema,
  "oms-priority": baseViewWithIdSchema,
  returns: baseViewWithNotificationsAndIdSchema,
  claims: baseViewWithNotificationsAndIdSchema,
  warranties: baseViewWithNotificationsAndIdSchema,
  recycle: baseViewWithNotificationsAndIdSchema,
  withdrawals: baseViewWithNotificationsAndIdSchema,
  registrations: baseViewWithIdSchema,
  "purchase-orders": baseViewWithIdSchema,
  shipments: baseViewWithIdSchema,
  transfers: baseViewWithIdSchema,
  "inventory-levels": baseViewWithIdSchema,
  "catalog-products": baseViewWithIdSchema,
  "order-tracking-shipments": baseViewWithIdSchema,
  "order-tracking-orders": baseViewWithIdSchema,
  "work-orders": baseViewWithIdSchema,
  "work-order-priority": pickSessionPriorityViewWithIdSchema,
  "replenishment-requests": baseViewWithIdSchema,
  "support-v2-conversations": baseViewWithIdSchema,
};

export type viewWithIdTypes = {
  [key in keyof typeof viewWithIdSchemas]: z.infer<
    (typeof viewWithIdSchemas)[key]
  >;
};

export type FulfillmentGroupView =
  viewWithIdTypes[ViewTypesNames.FULFILLMENT_GROUPS];

export type BulkFulfillmentView =
  viewWithIdTypes[ViewTypesNames.BULK_FULFILLMENTS];

export type PickSessionView = viewWithIdTypes[ViewTypesNames.PICK_SESSION];
export const PickSessionViewSchema =
  viewWithIdSchemas[ViewTypesNames.PICK_SESSION];

export type ReturnView = viewWithIdTypes[ViewTypesNames.RETURNS];
export type ClaimView = viewWithIdTypes[ViewTypesNames.CLAIMS];
export type WarrantyView = viewWithIdTypes[ViewTypesNames.WARRANTIES];
export type RecycleView = viewWithIdTypes[ViewTypesNames.RECYCLE];
export type WithdrawalView = viewWithIdTypes[ViewTypesNames.WITHDRAWALS];
export type RegistrationView = viewWithIdTypes[ViewTypesNames.REGISTRATIONS];
export type PurchaseOrderView = viewWithIdTypes[ViewTypesNames.PURCHASE_ORDERS];
export type ShipmentView = viewWithIdTypes[ViewTypesNames.SHIPMENTS];
export type TransferView = viewWithIdTypes[ViewTypesNames.TRANSFERS];
export type InventoryLevelsView =
  viewWithIdTypes[ViewTypesNames.INVENTORY_LEVELS];

export const ClientFulfillmentGroupView =
  viewWithIdSchemas["fulfillment-groups"];
export type ClientFulfillmentGroupView = z.infer<
  typeof ClientFulfillmentGroupView
>;

export const ClientBulkFulfillmentView = viewWithIdSchemas["bulk-fulfillments"];
export type ClientBulkFulfillmentView = z.infer<
  typeof ClientBulkFulfillmentView
>;

export const ClientReturnView = viewWithIdSchemas.returns;
export type ClientReturnView = z.infer<typeof ClientReturnView>;
export const ClientClaimView = viewWithIdSchemas.claims;
export type ClientClaimView = z.infer<typeof ClientClaimView>;
export const ClientWarrantyView = viewWithIdSchemas.warranties;
export type ClientWarrantyView = z.infer<typeof ClientWarrantyView>;
export const ClientRecycleView = viewWithIdSchemas.recycle;
export type ClientRecycleView = z.infer<typeof ClientRecycleView>;
export const ClientWithdrawalView = viewWithIdSchemas.withdrawals;
export type ClientWithdrawalView = z.infer<typeof ClientWithdrawalView>;
export const ClientRegistrationView = viewWithIdSchemas.registrations;
export type ClientRegistrationView = z.infer<typeof ClientRegistrationView>;
export const ClientPurchaseOrdersView = viewWithIdSchemas["purchase-orders"];
export type ClientPurchaseOrdersView = z.infer<typeof ClientPurchaseOrdersView>;
export const ClientShipmentView = viewWithIdSchemas.shipments;
export type ClientShipmentView = z.infer<typeof ClientShipmentView>;
export const ClientInventoryLevelsView = viewWithIdSchemas["inventory-levels"];
export type ClientInventoryLevelsView = z.infer<
  typeof ClientInventoryLevelsView
>;

export type CatalogProductsView =
  viewWithIdTypes[ViewTypesNames.CATALOG_PRODUCTS];
export const ClientCatalogProductsView = viewWithIdSchemas["catalog-products"];
export type ClientCatalogProductsView = z.infer<
  typeof ClientCatalogProductsView
>;

export type OrderTrackingShipmentView =
  viewWithIdTypes[ViewTypesNames.ORDER_TRACKING_SHIPMENTS];
export const ClientOrderTrackingShipmentView =
  viewWithIdSchemas["order-tracking-shipments"];
export type ClientOrderTrackingShipmentView = z.infer<
  typeof ClientOrderTrackingShipmentView
>;

export type OrderTrackingOrderView =
  viewWithIdTypes[ViewTypesNames.ORDER_TRACKING_ORDERS];
export const ClientOrderTrackingOrderView =
  viewWithIdSchemas["order-tracking-orders"];
export type ClientOrderTrackingOrderView = z.infer<
  typeof ClientOrderTrackingOrderView
>;

export type WorkOrderView = viewWithIdTypes[ViewTypesNames.WORK_ORDERS];
export const ClientWorkOrderView = viewWithIdSchemas["work-orders"];
export type ClientWorkOrderView = z.infer<typeof ClientWorkOrderView>;

export type ReplenishmentRequestView =
  viewWithIdTypes[ViewTypesNames.REPLENISHMENT_REQUESTS];
export const ClientReplenishmentRequestView =
  viewWithIdSchemas["replenishment-requests"];
export type ClientReplenishmentRequestView = z.infer<
  typeof ClientReplenishmentRequestView
>;

export type SupportV2ConversationView =
  viewWithIdTypes[ViewTypesNames.SUPPORT_V2_CONVERSATIONS];
export const ClientSupportV2ConversationView =
  viewWithIdSchemas["support-v2-conversations"];
export type ClientSupportV2ConversationView = z.infer<
  typeof ClientSupportV2ConversationView
>;

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { FilterOperator } from "./advanced-filters/generic-advanced-filter-data";
